import { useState, useEffect } from 'react'
import { selectableFocusAreas } from '../utils/specialtyResolver'
import { OverlayPortal } from '../components/ModalPortal'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { ArrowLeft, ChevronRight, FolderOpen, Plus, Sparkles } from 'lucide-react'
import useQuestionnaireStore from '../stores/useQuestionnaireStore'
import { useCaseStore } from '../stores'
import { casesAPI, aiAPI } from '../services/api'
import { useI18n } from '../i18n'
import { useQuestionnaireTranslation } from '../i18n/questionnaires/useQuestionnaireTranslation'
import { useAgent } from '../components/AgentContext'
import NotificationBellButton from '../components/NotificationBellButton'
import { caseDoctor } from '../utils/caseDoctor'

// Import logos
import orellaOIcon from '../assets/logos/orella_o_icon.png'

// Specialty icons — dynamic resolver (v133.21.0)
import { getSpecialtyIcon } from '../utils/specialtyIcons'
import { DIALOGUE_BUBBLE, DIALOGUE_PAD } from '../components/agent/dialogueBubble'
import { clinicianName } from '../utils/clinicianName.js'
import { OrellaSpinner } from '../components/OrellaLoader' // s123: the app's one spinner

// v229.1.0: Per-specialty topic phrase for the focus-page intro dialogue.
// Falls back to the lowercased display name for anything not listed.
const FOCUS_TOPIC = {
  ear: 'your ears',
  eye: 'your eyes',
  general: 'your general health',
  'head-neck': 'your head and neck',
  'health-wellness': 'your overall health and wellness',
  'muscle-joints': 'your muscles and joints',
  nose: 'your nose and sinuses',
  pain: 'chronic pain',
  skin: 'your skin',
  'stomach-gut': 'your stomach and digestion',
  throat: 'your throat',
  urinary: 'your urinary health',
  heart: 'your heart',
  lungs: 'your lungs',
  kidney: 'your kidneys',
  blood: 'your blood',
  'womens-health': "women's health",
  'brain-nerves': 'your brain and nerves',
  'mental-health': 'your mental health',
  vascular: 'your veins and circulation',
  infection: 'infections',
  sleep: 'your sleep',
  'plastic-surgery': 'plastic and reconstructive concerns',
  anesthesiology: 'anesthesia care',
}

// v230.4.5: Topics that are grammatically plural (or compound) take "them" in the
// intro line; everything else takes the singular "it". Drives intro vs introPlural.
const FOCUS_TOPIC_PLURAL = new Set([
  'ear', 'eye', 'head-neck', 'muscle-joints', 'nose', 'stomach-gut',
  'lungs', 'kidney', 'brain-nerves', 'vascular', 'infection', 'plastic-surgery',
  'feet', 'teeth',
])

// Typing dots component
function TypingDots() {
  return (
    <span className="inline-flex items-center gap-1 h-5">
      <span className="w-1.5 h-1.5 rounded-full bg-[#A7D9FC] animate-[dotPulse_1.2s_ease-in-out_infinite]" />
      <span className="w-1.5 h-1.5 rounded-full bg-[#A7D9FC] animate-[dotPulse_1.2s_ease-in-out_infinite_0.2s]" />
      <span className="w-1.5 h-1.5 rounded-full bg-[#A7D9FC] animate-[dotPulse_1.2s_ease-in-out_infinite_0.4s]" />
    </span>
  )
}

// v176.2.0: Status badge for existing cases
function StatusBadge({ status }) {
  const colors = {
    draft: 'bg-gray-100 text-gray-600',
    in_progress: 'bg-blue-50 text-blue-600',
    pending: 'bg-amber-50 text-amber-600',
    sent: 'bg-blue-50 text-blue-600',
    accepted: 'bg-blue-50 text-blue-600',
    appointment_scheduled: 'bg-green-50 text-green-600',
    in_follow_up: 'bg-amber-50 text-amber-600',
    observation: 'bg-slate-50 text-slate-600',
  }
  const label = (status || 'draft').replace(/_/g, ' ')
  return (
    <span className={`text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full ${colors[status] || colors.draft}`}>
      {label}
    </span>
  )
}

// ★★ s117 (D529, canvas "MD Panel Add Case Drawer" N2 + P2, approved 2026-10-23). The same header bar as the area
//   screen (back, the area's icon tile + name + "New case" / "For Dr. X", bell); the Orella dialogue asks "Which one
//   of the following is the main concern?" WITHOUT "Ask Orella anything" (his ruling); each row is the name and the
//   round sparkle Learn-more button (the pill and the chevron are gone). With `state.mdCaseId` the pick is written to
//   the case the physician started (PUT /cases/<id> specialty + focusArea) and the case goes to Describe.
export default function FocusAreas() {
  const { specialtyId } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const mdCaseId = location.state?.mdCaseId || null
  const [pickError, setPickError] = useState('')
  const { setAndSaveCase, cases: storeCases, loadCases } = useCaseStore()
  const [creating, setCreating] = useState(false)
  const [selectedId, setSelectedId] = useState(null)
  const { getSpecialty } = useQuestionnaireStore()
  const { t, language } = useI18n()
  const { tfa } = useQuestionnaireTranslation()
  const { openAgent } = useAgent()

  // v229.1.0: Per-concern "Learn more" — single-open accordion. Descriptions
  // are fetched on demand (Haiku) and cached so re-opening is instant.
  const [openConcernId, setOpenConcernId] = useState(null)
  const [concernDetails, setConcernDetails] = useState({}) // id -> text
  const [concernLoading, setConcernLoading] = useState({}) // id -> bool

  // v176.2.0: Existing case detection
  const [existingCases, setExistingCases] = useState(null) // null = not checked, [] = none found
  const [pendingFocusArea, setPendingFocusArea] = useState(null) // focus area waiting for user decision

  // Typing animation states
  const [showLogo, setShowLogo] = useState(false)
  const [showBubble, setShowBubble] = useState(false)
  const [showDots, setShowDots] = useState(false)
  const [showText, setShowText] = useState(false)
  const [showList, setShowList] = useState(false)

  const specialty = getSpecialty(specialtyId)

  // Run typing animation on mount
  useEffect(() => {
    const key = `orella_focus_animated_${specialtyId}`
    const hasPlayed = sessionStorage.getItem(key)
    if (hasPlayed) {
      setShowLogo(true)
      setShowBubble(true)
      setShowText(true)
      setShowList(true)
      return
    }

    const timers = []
    timers.push(setTimeout(() => setShowLogo(true), 200))
    timers.push(setTimeout(() => { setShowBubble(true); setShowDots(true) }, 400))
    timers.push(setTimeout(() => { setShowDots(false); setShowText(true) }, 1200))
    timers.push(setTimeout(() => { setShowList(true); sessionStorage.setItem(key, 'true') }, 1500))
    return () => timers.forEach(clearTimeout)
  }, [specialtyId])
  
  if (!specialty) {
    return (
      <div className="min-h-full bg-white p-6 flex flex-col items-center justify-center">
        <p className="text-gray-500 mb-4">{t('errors.somethingWrong')}</p>
        <button onClick={() => navigate('/home')} className="text-orella-500 font-medium">
          {t('common.back')}
        </button>
      </div>
    )
  }

  const displayName = specialty.id === 'general'
    ? t('home.generalComplaint', 'General complaint')
    : (t(`specialties.${specialty.id}`) || specialty.name)
  const iconSrc = getSpecialtyIcon(specialty)
  const mdDoctor = mdCaseId ? caseDoctor((storeCases || []).find((c) => c.id === mdCaseId)) : null
  const subLine = mdDoctor
    ? t('home.forDoctor', { defaultValue: 'For {{doctor}}', doctor: clinicianName(mdDoctor) })
    : t('home.newCase', 'New case')

  // v229.1.0: Topic-aware intro for the Orella dialogue bubble.
  const topic = t(`focusAreas.introTopics.${specialty.id}`, {
    defaultValue: FOCUS_TOPIC[specialty.id] || (displayName || specialty.name || '').toLowerCase(),
  })
  // v230.4.5: Pick singular ("...affect it") vs plural ("...affect them") intro
  // copy so the pronoun agrees with the topic instead of always reading "them".
  const introIsPlural = FOCUS_TOPIC_PLURAL.has(specialty.id)
  // s99 D477 (Build 61 TestFlight, Dr. Rami 2026-10-11): the bubble asks the question the screen
  //   is for. "Ask Orella anything" stays under it (his ruling). introText (the old topic intro)
  //   is no longer rendered; it stays computed so a revert is one line.
  const introQuestion = t('focusAreas.whichMainConcern', { defaultValue: 'Which one of the following is the main concern?' })
  const introText = introIsPlural
    ? t('focusAreas.introPlural', {
        defaultValue: 'I can tell you more about {{topic}} and the different conditions that can affect them — or pick a concern below to get started.',
        topic,
      })
    : t('focusAreas.intro', {
        defaultValue: 'I can tell you more about {{topic}} and the different conditions that can affect it — or pick a concern below to get started.',
        topic,
      })

  // Toggle a concern's "Learn more" panel (single-open accordion). Fetches the
  // short description once, then caches it.
  const toggleLearnMore = async (focus) => {
    if (openConcernId === focus.id) { setOpenConcernId(null); return }
    setOpenConcernId(focus.id)
    if (concernDetails[focus.id]) return // already loaded
    setConcernLoading((m) => ({ ...m, [focus.id]: true }))
    try {
      const resp = await aiAPI.getConcernDetail(focus.name, specialty.name, language)
      const text = resp?.detail
      setConcernDetails((m) => ({
        ...m,
        [focus.id]: text || t('focusAreas.learnMoreUnavailable', {
          defaultValue: "I couldn't load a description right now — but you can still tap this concern to get started.",
        }),
      }))
    } catch {
      setConcernDetails((m) => ({
        ...m,
        [focus.id]: t('focusAreas.learnMoreUnavailable', {
          defaultValue: "I couldn't load a description right now — but you can still tap this concern to get started.",
        }),
      }))
    } finally {
      setConcernLoading((m) => ({ ...m, [focus.id]: false }))
    }
  }

  // "Ask a follow-up" on a concern → opens the Orella chat pop-up preloaded.
  const askAboutConcern = (focus) => {
    openAgent(
      t('focusAreas.askAboutPrefill', {
        defaultValue: 'Can you tell me more about {{concern}}?',
        concern: tfa(focus.name),
      }),
      'focus_concern',
    )
  }

  const handleSelectFocus = async (focusArea) => {
    // s117 (D529): the case the physician started takes the area and focus, then the urgent screen and Describe.
    if (mdCaseId) {
      setCreating(true)
      setPickError('')
      try {
        await casesAPI.update(mdCaseId, { specialty: specialty.name, focusArea: focusArea.name })
        const base = (storeCases || []).find((c) => c.id === mdCaseId) || { id: mdCaseId }
        setAndSaveCase({
          ...base,
          specialty: { id: specialty.id, name: specialty.name },
          specialtyId: specialty.id,
          specialtyName: specialty.name,
          focusArea: { id: focusArea.id, name: focusArea.name },
          focusAreaId: focusArea.id,
          focusAreaName: focusArea.name,
          emergencyScreeningPassed: false,
        })
        navigate('/case/emergency', { replace: true })
      } catch {
        setPickError(t('errors.somethingWrong'))
      } finally {
        setCreating(false)
      }
      return
    }
    // v201.2: Do NOT create case or call API yet — defer until emergency screen is cleared.
    // Pass pending data via nav state; Emergency.jsx creates the draft only on proceed.
    setSelectedId(focusArea.id)
    navigate(`/case/emergency`, {
      state: {
        pendingCase: {
          specialtyId: specialty.id,
          specialtyName: specialty.name,
          focusAreaId: focusArea.id,
          focusAreaName: focusArea.name,
        }
      }
    })
    setSelectedId(null)
  }

  // v176.2.0: User chose to continue with the new case (dismiss existing cases modal)
  const handleProceedNewCase = () => {
    if (!pendingFocusArea) return
    const { focusArea } = pendingFocusArea
    setExistingCases(null)
    setPendingFocusArea(null)
    navigate(`/case/emergency`, {
      state: {
        pendingCase: {
          specialtyId: specialty.id,
          specialtyName: specialty.name,
          focusAreaId: focusArea.id,
          focusAreaName: focusArea.name,
        }
      }
    })
  }

  // v176.2.0: User chose to resume an existing case
  const handleResumeCase = async (existingCase) => {
    setExistingCases(null)
    setPendingFocusArea(null)
    // Try to find the full case in the store first
    const fullCase = storeCases.find(c => c.id === existingCase.id)
    if (fullCase) {
      setAndSaveCase(fullCase)
      navigate('/case/home')
    } else {
      // Reload cases from API to pick it up, then navigate to cases list
      try { await loadCases() } catch (e) { console.error('Failed to reload cases:', e) }
      navigate('/cases')
    }
  }

  return (
    <div className="min-h-full bg-white flex flex-col">
      {/* Typing animation keyframes */}
      <style>{`
        @keyframes dotPulse {
          0%, 80%, 100% { opacity: 0.3; transform: scale(0.8); }
          40% { opacity: 1; transform: scale(1.1); }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
      `}</style>

      {/* s117: one header bar - back, the area, bell */}
      <div className="sticky-page-header px-2 h-[60px] flex items-center gap-2.5" style={{ borderBottom: '1px solid #E7EEF4' }}>
        <button
          onClick={() => navigate('/specialties', mdCaseId ? { state: { mdCaseId } } : undefined)}
          className="w-11 h-11 flex items-center justify-center text-gray-600"
          aria-label={t('common.back', 'Back')}
        >
          <ArrowLeft size={22} />
        </button>
        <span className="w-[38px] h-[38px] rounded-[11px] flex items-center justify-center flex-shrink-0 bg-[#E9F8FF]">
          {iconSrc && <img src={iconSrc} alt="" className="w-[28px] h-[28px] object-contain" />}
        </span>
        <span className="flex-1 min-w-0">
          <h1 className="text-[16px] font-extrabold leading-tight text-orella-700 truncate">{displayName}</h1>
          <span className="block text-[13px] font-medium text-[#5F7A8E] truncate">{subLine}</span>
        </span>
        <NotificationBellButton variant="compact" />
      </div>

      {/* Orella dialogue — the question this screen is for (s117: no "Ask Orella anything") */}
      <div className="px-4 pt-4 pb-4">
        <div className="flex items-start gap-3">
          <img 
            src={orellaOIcon} 
            alt="Orella" 
            className={`w-10 h-10 rounded-lg flex-shrink-0 object-contain transition-all duration-400 ${showLogo ? 'opacity-100 scale-100' : 'opacity-0 scale-75'}`}
          />
          <div className={`flex-1 ${DIALOGUE_BUBBLE} ${DIALOGUE_PAD} transition-all duration-400 ${showBubble ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-1 scale-95'}`}>
            {showDots && !showText && <TypingDots />}
            {showText && (
              <p className="text-[15.5px] font-medium text-orella-700 leading-relaxed animate-[fadeIn_0.3s_ease]">
                {introQuestion}
              </p>
            )}
          </div>
        </div>
        {pickError && <p className="text-[13px] font-semibold text-red-600 mt-3">{pickError}</p>}
      </div>

      {/* Focus Areas List */}
      <div className={`flex-1 px-4 pb-8 relative transition-all duration-500 ${showList ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3'}`}>
        {creating && (
          <div className="absolute inset-0 bg-white/80 flex items-center justify-center z-10 rounded-2xl">
            <div className="flex items-center gap-2 text-orella-600">
              <OrellaSpinner size={24} />
              <span className="font-medium">{t('common.loading')}</span>
            </div>
          </div>
        )}
        <div>
          {/* s100: retired focus areas stay in the catalog for old cases but are not offered (specialtyResolver owns the test) */}
          {selectableFocusAreas(specialty).map((focus) => {
            const isOpen = openConcernId === focus.id
            const isLoading = !!concernLoading[focus.id]
            const detail = concernDetails[focus.id]
            return (
              <div
                key={focus.id}
                className={`bg-white overflow-hidden transition-all duration-200
                           ${isOpen
                             ? 'rounded-xl border-2 border-[#A7D9FC] shadow-md shadow-[#46A8F9]/10 my-1'
                             : 'border-b border-[#EEF3F7]'}`}
              >
                {/* Row — s117: the same row as the area list (name + sparkle), no chevron */}
                <div className={`flex items-center gap-3 py-3 min-h-[60px] ${isOpen ? 'px-4' : 'px-0.5'}`}>
                  <button
                    onClick={() => handleSelectFocus(focus)}
                    disabled={creating}
                    className="flex-1 min-w-0 text-left disabled:opacity-50 disabled:cursor-not-allowed active:opacity-70 transition-opacity"
                  >
                    <span className="font-bold text-orella-700 text-[15px]">{tfa(focus.name)}</span>
                  </button>
                  <button
                    onClick={() => toggleLearnMore(focus)}
                    aria-label={`${isOpen ? t('focusAreas.hide', 'Hide') : t('focusAreas.learnMore', 'Learn more')}: ${tfa(focus.name)}`}
                    aria-expanded={isOpen}
                    className="w-[34px] h-[34px] rounded-full flex items-center justify-center bg-[#E9F8FF] border border-[#DCEFFB] text-[#46A8F9] active:scale-[0.94] transition-transform flex-shrink-0"
                  >
                    <Sparkles size={14} />
                  </button>
                </div>

                {/* Learn more panel */}
                {isOpen && (
                  <div className="px-4 pb-3.5">
                    <div className="bg-[#E9F8FF] border border-[#d6ecfa] rounded-xl px-3.5 py-3 flex gap-2.5">
                      <img src={orellaOIcon} alt="" className="w-6 h-6 rounded-md object-contain flex-shrink-0 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        {isLoading ? (
                          <TypingDots />
                        ) : (
                          <p className="text-[12.8px] leading-relaxed text-[#2b4a63] font-medium">{detail}</p>
                        )}
                        {!isLoading && detail && (
                          <div className="flex flex-wrap gap-2 mt-2.5">
                            <button
                              onClick={() => handleSelectFocus(focus)}
                              disabled={creating}
                              className="text-[11.5px] font-bold rounded-full px-3 py-1.5 bg-[#46A8F9] text-white active:scale-[0.96] transition-transform disabled:opacity-50"
                            >
                              {t('focusAreas.startCaseForThis', 'Start a case for this')}
                            </button>
                            <button
                              onClick={() => askAboutConcern(focus)}
                              className="text-[11.5px] font-bold rounded-full px-3 py-1.5 bg-white border border-[#A7D9FC] text-[#46A8F9] active:scale-[0.96] transition-transform"
                            >
                              {t('focusAreas.askFollowUp', 'Ask a follow-up')}
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </div>

      {/* v176.2.0: Existing Cases Modal — shown when duplicate specialty case detected */}
      {existingCases && existingCases.length > 0 && (
        <OverlayPortal>
        <div className="fixed inset-0 z-[100] flex items-end justify-center" onClick={() => { setExistingCases(null); setPendingFocusArea(null) }}>
          <div className="absolute inset-0 bg-black/40 animate-[fadeIn_0.2s_ease]" />
          <div
            className="relative w-full max-w-lg bg-white rounded-t-3xl px-5 pt-5 pb-8 bottom-actions [--ba-min:32px] max-h-[75vh] overflow-y-auto animate-[slideUp_0.3s_ease]"
            onClick={(e) => e.stopPropagation()}
          >
            <style>{`@keyframes slideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }`}</style>
            {/* Handle */}
            <div className="w-10 h-1 bg-gray-200 rounded-full mx-auto mb-4" />

            {/* Orella bubble */}
            <div className="flex items-start gap-3 mb-4">
              <img src={orellaOIcon} alt="Orella" className="w-9 h-9 rounded-lg flex-shrink-0 object-contain" />
              <div className={`flex-1 ${DIALOGUE_BUBBLE} ${DIALOGUE_PAD}`}>
                <p className="text-[15.5px] font-medium text-[#15354F] leading-relaxed">
                  {t('focusAreas.existingCaseFound')}
                </p>
              </div>
            </div>

            {/* Existing cases list */}
            <div className="space-y-2 mb-4">
              {existingCases.map((ec) => {
                const createdDate = ec.created_at ? new Date(ec.created_at).toLocaleDateString() : ''
                return (
                  <button
                    key={ec.id}
                    onClick={() => handleResumeCase(ec)}
                    className="w-full bg-white rounded-xl px-4 py-3 flex items-center gap-3 border border-gray-200 hover:border-[#46A8F9] hover:bg-[#E9F8FF]/30 active:scale-[0.99] transition-all text-left"
                  >
                    <div className="w-10 h-10 bg-[#E9F8FF] rounded-xl flex items-center justify-center flex-shrink-0">
                      <FolderOpen size={18} className="text-[#46A8F9]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[13px] font-bold text-[#15354F] truncate">
                        {ec.focus_area || ec.specialty} {ec.doctorName ? `— ${ec.doctorName}` : ''}
                      </p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <StatusBadge status={ec.status} />
                        {createdDate && <span className="text-[11px] text-gray-400">{createdDate}</span>}
                      </div>
                    </div>
                    <ChevronRight size={16} className="text-gray-300 flex-shrink-0" />
                  </button>
                )
              })}
            </div>

            {/* Start new case button */}
            <button
              onClick={handleProceedNewCase}
              className="w-full flex items-center justify-center gap-2 py-3 rounded-xl border-[1.5px] border-gray-200 text-[#15354F] font-semibold text-[14px] active:scale-[0.99] transition-all"
            >
              <Plus size={16} />
              {t('focusAreas.startNewCaseInstead')}
            </button>
          </div>
        </div>
        </OverlayPortal>
      )}
    </div>
  )
}
